/**
 * @file useDashboardSettings.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-02
 * @modified 2026-10-30
 *
 * Hook for fetching and saving dashboard settings.
 *
 * Honours the server's `locked` flag: a REP badge gets a fixed layout from
 * config/repDashboardLayout.js and every write is refused with LAYOUT_LOCKED,
 * so the writers here return early rather than firing a doomed request.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';
import { defaultPlacements } from '../widgets';

const DEFAULT_LAYOUT = 'narrow-left-two-right';

function useDashboardSettings() {
  const [settings, setSettings] = useState({
    layout_preset: DEFAULT_LAYOUT,
    widget_placement: defaultPlacements[DEFAULT_LAYOUT],
    widget_settings: {},
    // The server sets this for a REP badge: the layout is a code constant in
    // config/repDashboardLayout.js and every write is refused with LAYOUT_LOCKED.
    // We short-circuit the saves here so the UI never fires a request it knows
    // will be refused — the server is still the authority, this is just manners.
    locked: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // A ref rather than a dep so the save callbacks keep a stable identity —
  // DashboardConfigurator holds onto them through an imperative handle.
  const lockedRef = useRef(false);

  /** Refuse a write the server would refuse anyway, without the round trip. */
  const blockedByLock = () => {
    if (!lockedRef.current) return null;
    return { success: false, error: 'This dashboard layout is fixed', code: 'LAYOUT_LOCKED' };
  };

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSettings({
          layout_preset: data.layout_preset || DEFAULT_LAYOUT,
          widget_placement: data.widget_placement || defaultPlacements[data.layout_preset || DEFAULT_LAYOUT],
          widget_settings: data.widget_settings || {},
          locked: data.locked === true,
          // The rep layout's own phone order and the strip above the grid
          // (config/repDashboardLayout.js). phone_order was dropped here, so
          // the phone fell back to the columns' order; kept now (2026-10-30).
          ...(Array.isArray(data.phone_order) ? { phone_order: data.phone_order } : {}),
          ...(Array.isArray(data.top_strip) ? { top_strip: data.top_strip } : {})
        });
        lockedRef.current = data.locked === true;
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (err) {
      console.error('Error fetching dashboard settings:', err);
      setError(err.message);
      // Use defaults on error
      setSettings({
        layout_preset: DEFAULT_LAYOUT,
        widget_placement: defaultPlacements[DEFAULT_LAYOUT],
        widget_settings: {},
        locked: false
      });
      lockedRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Save full settings (layout + widget placement)
  const saveSettings = useCallback(async (newSettings) => {
    const blocked = blockedByLock();
    if (blocked) return blocked;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          layout_preset: data.settings.layout_preset,
          widget_placement: data.settings.widget_placement,
          widget_settings: data.settings.widget_settings
        }));
        return { success: true };
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving dashboard settings:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  // Save single widget settings (for zoom, selected tab, etc.)
  const saveWidgetSettings = useCallback(async (widgetId, widgetSettings) => {
    const blocked = blockedByLock();
    if (blocked) return blocked;
    try {
      const response = await fetch(`${API_URL}/api/dashboard/widget-settings/${widgetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(widgetSettings)
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          widget_settings: data.widget_settings
        }));
        return { success: true };
      } else {
        throw new Error('Failed to save widget settings');
      }
    } catch (err) {
      console.error('Error saving widget settings:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Reset to defaults
  const resetSettings = useCallback(async () => {
    const blocked = blockedByLock();
    if (blocked) return blocked;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/settings`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          layout_preset: data.defaults.layout_preset,
          widget_placement: data.defaults.widget_placement,
          widget_settings: data.defaults.widget_settings
        }));
        return { success: true };
      } else {
        throw new Error('Failed to reset settings');
      }
    } catch (err) {
      console.error('Error resetting dashboard settings:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  // Update local settings without saving (for preview)
  const updateLocalSettings = useCallback((newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  return {
    settings,
    loading,
    error,
    saving,
    saveSettings,
    saveWidgetSettings,
    resetSettings,
    updateLocalSettings,
    refetch: fetchSettings
  };
}

export default useDashboardSettings;
